"use client";

import { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import Navbar from "@/app/Components/NavbarSection";
import Footer from "@/app/Sections/Footer";
import { Search, ChevronRight } from "lucide-react";
import Image from "next/image";

const Features = dynamic(() => import("@/app/Sections/FeaturesSection"));
const Reviews = dynamic(() => import("@/app/Sections/ReviewsSection"));
const LocationSection = dynamic(() => import("@/app/Sections/LocationSection"));

type Game = {
    name: string;
    slug: string;
    logo: string;
    href: string;
    startingPrice: number;
    tags: string[];
    tier: "top" | "more";
};

const games: Game[] = [
    // --- TOP GAMES ---
    {
        name: "Minecraft",
        slug: "minecraft-servers",
        logo: "https://cdn.aurex.lk/games/mc-logo.webp",
        href: "/minecraft-servers",
        startingPrice: 400,
        tags: ["Sandbox", "Survival", "Multiplayer"],
        tier: "top",
    },
    {
        name: "FiveM",
        slug: "fivem-servers",
        logo: "https://cdn.aurex.lk/fivem/fm-left.webp",
        href: "/fivem-servers",
        startingPrice: 1600,
        tags: ["Action", "Roleplay", "Sandbox"],
        tier: "top",
    },
    {
        name: "Rust",
        slug: "rust-servers",
        logo: "https://cdn.aurex.lk/rust/rust-left.webp",
        href: "/rust-servers",
        startingPrice: 2400,
        tags: ["Survival", "Multiplayer", "PVP"],
        tier: "top",
    },
    {
        name: "Hytale",
        slug: "hytale-servers",
        logo: "https://cdn.aurex.lk/games/hytale.webp",
        href: "/hytale-servers",
        startingPrice: 2400,
        tags: ["Adventure", "RPG", "Sandbox"],
        tier: "top",
    },
    // --- MORE GAMES ---
    {
        name: "MTA",
        slug: "mta-servers",
        logo: "https://cdn.aurex.lk/games/mta.webp",
        href: "/mta-servers",
        startingPrice: 400,
        tags: ["Action", "Roleplay", "Sandbox"],
        tier: "more",
    },
    {
        name: "Valheim",
        slug: "valheim-servers",
        logo: "https://dash.aurex.lk/storage/categories/wQiHwHqov1kyZqKKPa8I3aDA6Nxba0PZTbvnfFhW.png",
        href: "/valheim-servers",
        startingPrice: 1600,
        tags: ["Survival", "Multiplayer", "Sandbox"],
        tier: "more",
    },
];

const topGames = games.filter((g) => g.tier === "top");
const moreGames = games.filter((g) => g.tier === "more");

/** MORE GAMES rows are centered so a partial row tapers into a pyramid. */
const MORE_PER_ROW = 4;
const moreRows: Game[][] = moreGames.reduce((rows, game, i) => {
    const rowIndex = Math.floor(i / MORE_PER_ROW);
    if (!rows[rowIndex]) rows[rowIndex] = [];
    rows[rowIndex].push(game);
    return rows;
}, [] as Game[][]);

function PriceTag({ price, className }: { price: number; className?: string }) {
    return (
        <p className={`text-white/60 font-poppins text-[10px] uppercase tracking-widest mt-1 ${className ?? ""}`}>
            Starting from <span className="text-green-400 font-semibold">LKR {price}</span>/mo
        </p>
    );
}

function CardLink({ game, className, children }: { game: Game; className: string; children: React.ReactNode }) {
    const isExternal = game.href.startsWith("http");
    return (
        <a
            href={game.href}
            {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className={className}
        >
            {children}
        </a>
    );
}

function GameCard({ game }: { game: Game }) {
    return (
        <CardLink
            game={game}
            className="group relative rounded-2xl overflow-hidden block aspect-[3/4] sm:aspect-[2/3]"
        >
            <Image
                src={game.logo}
                alt={game.name}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                className="absolute inset-0 object-cover transition-transform duration-500 group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-end justify-between gap-2">
                    <div>
                        <h3 className="text-white font-akira text-lg leading-tight drop-shadow-lg">{game.name}</h3>
                        <PriceTag price={game.startingPrice} />
                    </div>
                    <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 translate-x-2">
                        <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                </div>
            </div>
        </CardLink>
    );
}

function MoreGameCard({ game }: { game: Game }) {
    return (
        <CardLink
            game={game}
            className="group relative rounded-2xl overflow-hidden block aspect-[3/4] w-[calc(50%-0.55rem)] lg:w-[calc(25%-1.25rem)]"
        >
            <Image
                src={game.logo}
                alt={game.name}
                fill
                sizes="(max-width: 1024px) 50vw, 25vw"
                className="absolute inset-0 object-cover transition-transform duration-500 group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-akira text-base leading-tight drop-shadow-lg">{game.name}</h3>
                <PriceTag price={game.startingPrice} />
            </div>
        </CardLink>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="flex items-center gap-3 font-akira text-2xl sm:text-3xl text-white mb-6">
            <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-purple-400 to-purple-800" />
            {children}
        </h2>
    );
}

export default function GamesPage() {
    const [query, setQuery] = useState("");

    const isSearching = query.trim().length > 0;

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        return games.filter(
            (g) => g.name.toLowerCase().includes(q) || g.tags.some((t) => t.toLowerCase().includes(q))
        );
    }, [query]);

    return (
        <div className="min-h-screen bg-black font-poppins">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0 opacity-30"
                        style={{
                            backgroundImage:
                                "radial-gradient(ellipse 70% 50% at 50% 0%, #6416a3 0%, transparent 70%)",
                        }}
                    />
                    <div
                        className="absolute inset-0 opacity-10"
                        style={{
                            backgroundImage:
                                "repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.03) 39px, rgba(255,255,255,0.03) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.03) 39px, rgba(255,255,255,0.03) 40px)",
                        }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-16 text-center">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-tl-xl rounded-br-xl bg-purple-500 border border-purple-500/20 text-white text-xs font-medium uppercase tracking-widest">
                            3 worldwide locations, instant setup
                        </div>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight mb-4">
                            Game Server
                            <span
                                className="block bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(90deg, #8B5CF6 0%, #a855f7 50%, #c084fc 100%)" }}
                            >
                                Hosting
                            </span>
                        </h1>
                        <p className="text-gray-400 font-poppins text-sm sm:text-base max-w-xl mx-auto mb-10">
                            Deploy your server in seconds. No setup fees, no contracts, just pure performance.
                        </p>

                        <div className="relative max-w-md mx-auto">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search games..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="w-full bg-white/5 border border-white/5 rounded-full pl-11 pr-5 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-purple-500/10 focus:bg-white/5 transition-all"
                            />
                        </div>
                    </div>
                </div>

                <div
                    className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
                    style={{ background: "linear-gradient(to bottom, transparent, black)" }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 sm:px-8 pb-24">
                {isSearching ? (
                    results.length > 0 ? (
                        <div>
                            <p className="text-xs text-gray-500 font-poppins mb-5">
                                {results.length} game{results.length !== 1 ? "s" : ""} found
                            </p>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                                {results.map((game) => (
                                    <GameCard key={game.slug} game={game} />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
                            <span className="text-4xl">🔍</span>
                            <p className="text-white font-poppins font-medium">No games found</p>
                            <p className="text-gray-500 font-poppins text-sm">
                                Try a different search term.
                            </p>
                            <button
                                onClick={() => setQuery("")}
                                className="mt-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                            >
                                Clear search
                            </button>
                        </div>
                    )
                ) : (
                    <div className="space-y-24">
                        <section>
                            <SectionTitle>TOP GAMES</SectionTitle>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                {topGames.map((game) => (
                                    <GameCard key={game.slug} game={game} />
                                ))}
                            </div>
                        </section>

                        <section>
                            <SectionTitle>MORE GAMES</SectionTitle>
                            <div className="flex flex-col gap-5">
                                {moreRows.map((row, rowIndex) => (
                                    <div
                                        key={rowIndex}
                                        className="flex flex-wrap justify-center gap-4 lg:gap-6"
                                    >
                                        {row.map((game) => (
                                            <MoreGameCard key={game.slug} game={game} />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </div>

            <Features />
            <Reviews />
            <LocationSection />

            <Footer />
        </div>
    );
}
